import { PageContainer } from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { PAGE_STACK } from './calibrationStyles';
import { BulkAdjustmentModal } from './components/BulkAdjustmentModal';
import { CalibrationCyclesPlaceholder } from './components/CalibrationCyclesPlaceholder';
import { CalibrationFilters } from './components/CalibrationFilters';
import { CalibrationHeader } from './components/CalibrationHeader';
import { CalibrationReportContent } from './components/CalibrationReportContent';
import { PublishAdjustmentsModal } from './components/PublishAdjustmentsModal';
import { ViewTiles } from './components/ViewTiles';
import { DEFAULT_MANAGER_PIVOT, isCalibrationView } from './utils/calibrationViews';
import { buildJobTitleLabels } from './utils/jobTitleLabels';
import { countDraftAdjustments } from './utils/reportAdjustments';
import { toReportFilters } from './utils/toReportFilters';

import type { CalibrationView } from '@/types/calibration';

import { useGetCalibrationCycles, useGetCalibrationReport } from '@/api/calibration';
import { useGetCompanyInformation } from '@/api/directory';
import { Permission } from '@/constants/Permission';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import {
    defaultFiltersState,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';

const CYCLE_PARAM = 'cycle';
const VIEW_PARAM = 'view';

interface SearchParamUpdate {
    key: typeof CYCLE_PARAM | typeof VIEW_PARAM;
    value: string;
}

/**
 * Calibration report container: orchestration only. Owns the cycle, view and filter selection
 * (cycle and view live in the URL so a reload or a shared link lands back on the same slice),
 * loads cycles and the report they drive, and distributes both to the header, the view tiles, the
 * filter bar and the report content.
 */
export const CalibrationReportPage = () => {
    const language = useSelectedAppLanguage();
    const history = useHistory();
    const location = useLocation();
    const { can } = useUserPermissions();
    const canManageAdjustments = can(Permission.ManageAllDepartmentsPerformance);

    const { data: company } = useGetCompanyInformation();
    const {
        data: cycles,
        isLoading: isCyclesLoading,
        isError: isCyclesError,
    } = useGetCalibrationCycles();

    const [filters, setFilters] = useState<SelectedFilters>(defaultFiltersState);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const cycleFromUrl = searchParams.get(CYCLE_PARAM);
    const viewFromUrl = searchParams.get(VIEW_PARAM);

    const selectedCycleId = cycleFromUrl ?? cycles?.[0]?.id;
    // A URL naming a view the caller cannot open — `employees` without the adjustment
    // permission — falls back to the default rather than to a view tile that is not even shown.
    const canOpenViewFromUrl =
        isCalibrationView(viewFromUrl) && (viewFromUrl !== 'employees' || canManageAdjustments);
    const activeView: CalibrationView = canOpenViewFromUrl ? viewFromUrl : DEFAULT_MANAGER_PIVOT;

    const updateSearchParams = ({ key, value }: SearchParamUpdate) => {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set(key, value);
        history.replace({ search: nextParams.toString() });
    };

    const reportFilters = useMemo(() => toReportFilters({ filters, company }), [filters, company]);
    const jobTitleLabels = useMemo(() => buildJobTitleLabels(company), [company]);

    const {
        data: report,
        isLoading: isReportLoading,
        isError: isReportError,
    } = useGetCalibrationReport({ cycleEvaluationId: selectedCycleId, filters: reportFilters });

    useFetchErrorsHandler({ errors: [isCyclesError, isReportError] });

    const selectedCycle = cycles?.find((cycle) => cycle.id === selectedCycleId);
    const hasCycles = !!cycles && cycles.length > 0;

    const changeView = (view: CalibrationView) =>
        updateSearchParams({ key: VIEW_PARAM, value: view });

    return (
        <PageContainer>
            <div className={PAGE_STACK}>
                <CalibrationHeader
                    cycles={cycles ?? []}
                    selectedCycleId={selectedCycleId ?? ''}
                    onCycleChange={(cycleId) =>
                        updateSearchParams({ key: CYCLE_PARAM, value: cycleId })
                    }
                    language={language}
                />

                {hasCycles ? (
                    <>
                        <ViewTiles
                            activeView={activeView}
                            onViewChange={changeView}
                            canViewEmployees={canManageAdjustments}
                        />

                        <CalibrationFilters
                            company={company}
                            filters={filters}
                            onFiltersChange={setFilters}
                        />

                        <CalibrationReportContent
                            report={report}
                            isLoading={isReportLoading}
                            isError={isReportError}
                            activeView={activeView}
                            onViewChange={changeView}
                            canManageAdjustments={canManageAdjustments}
                            language={language}
                            jobTitleLabels={jobTitleLabels}
                            onBulkAdjust={() => setIsBulkModalOpen(true)}
                            onPublish={() => setIsPublishModalOpen(true)}
                        />
                    </>
                ) : (
                    <CalibrationCyclesPlaceholder
                        isLoading={isCyclesLoading}
                        isError={isCyclesError}
                    />
                )}
            </div>

            {isBulkModalOpen && report && (
                <BulkAdjustmentModal
                    isOpen
                    onClose={() => setIsBulkModalOpen(false)}
                    report={report}
                    filters={reportFilters}
                    cycleEvaluationId={report.cycleEvaluationId}
                    language={language}
                />
            )}

            {isPublishModalOpen && report && selectedCycle && (
                <PublishAdjustmentsModal
                    isOpen
                    onClose={() => setIsPublishModalOpen(false)}
                    cycleEvaluationId={report.cycleEvaluationId}
                    cycleTitle={selectedCycle.title}
                    draftCount={countDraftAdjustments(report)}
                />
            )}
        </PageContainer>
    );
};
